import { z } from 'zod';
import { isoDateSchema, objectIdSchema } from './common.js';
import { journeySummarySchema, storyResponseSchema } from './content.js';
import { unlockedAchievementSchema, userJourneyStatusSchema } from './journey.js';
/**
 * COLLECTION journeys — the contract.
 *
 * A collection is a library, not a schedule:
 *
 *   JOURNEY (kind COLLECTION) → SECTION → SUB-JOURNEY → EPISODE → STORY (the audio)
 *
 * Thirukkural is the first one: three sections (அறம் · பொருள் · இன்பம்), 133 adhikarams as
 * sub-journeys, 1330 kurals as episodes. Nothing locks and nothing is earned in sequence —
 * a user searches or browses and plays whatever they like. What IS kept is the loop: an
 * episode played to its end is a completion, which earns the day's streak and counts as a
 * story finished, exactly as a daily journey's day does. The journey itself completes only
 * when every episode has.
 *
 * Episodes carry their own text (two lines and a meaning, for a kural) and lean on a Story
 * row for the audio and banner, so the upload, playback and art flows are the same ones
 * the portal already has.
 */
/* ───────────────────────── admin: sections ───────────────────────── */
export const sectionResponseSchema = z.object({
    id: objectIdSchema,
    journeyId: objectIdSchema,
    /** 1-based position within the journey. The server renumbers on reorder. */
    order: z.number().int(),
    title: z.string(),
    description: z.string(),
    /** A short glyph for the section card — an emoji, or empty. The image wins when set. */
    icon: z.string(),
    /** The section's image, square like every other banner. Signed per read. */
    imageUrl: z.string().nullable(),
    imageWidth: z.number().int().nullable(),
    imageHeight: z.number().int().nullable(),
    hasImage: z.boolean(),
    subJourneyCount: z.number().int(),
    episodeCount: z.number().int(),
    /** Episodes that have audio — what "built" means for a section. */
    readyCount: z.number().int(),
    createdAt: isoDateSchema,
});
export const createSectionBodySchema = z.object({
    title: z.string().trim().min(1).max(140),
    description: z.string().trim().max(2000).default(''),
    icon: z.string().trim().max(8).default(''),
});
export const updateSectionBodySchema = z
    .object({
    title: z.string().trim().min(1).max(140).optional(),
    description: z.string().trim().max(2000).optional(),
    icon: z.string().trim().max(8).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
/** Every section of the journey, in the wanted order. The server renumbers 1..N. */
export const reorderSectionsBodySchema = z.object({
    sectionIds: z.array(objectIdSchema).min(1),
});
/* ───────────────────────── admin: sub-journeys ───────────────────────── */
/**
 * A sub-journey's number is unique within the journey and is what orders it, across every
 * section — adhikaram 40 is adhikaram 40 whichever section it sits in. Editable, because
 * the numbering is canonical content, not something the portal invents.
 */
export const subJourneyResponseSchema = z.object({
    id: objectIdSchema,
    journeyId: objectIdSchema,
    sectionId: objectIdSchema,
    number: z.number().int().min(1),
    title: z.string(),
    description: z.string(),
    episodeCount: z.number().int(),
    readyCount: z.number().int(),
    /** Sum of the episodes' audio, so the app can say "about 20 mins". */
    durationSec: z.number().int(),
    /**
     * The adhikaram's banner — square, like a story banner, and what every episode under it
     * shows in the player unless the episode has art of its own. Signed per read.
     */
    bannerUrl: z.string().nullable(),
    bannerWidth: z.number().int().nullable(),
    bannerHeight: z.number().int().nullable(),
    hasBanner: z.boolean(),
    createdAt: isoDateSchema,
});
export const createSubJourneyBodySchema = z.object({
    sectionId: objectIdSchema,
    /** Omit to take the next free number in the journey. */
    number: z.number().int().min(1).max(100_000).optional(),
    title: z.string().trim().min(1).max(140),
    description: z.string().trim().max(2000).default(''),
});
export const updateSubJourneyBodySchema = z
    .object({
    sectionId: objectIdSchema.optional(),
    number: z.number().int().min(1).max(100_000).optional(),
    title: z.string().trim().min(1).max(140).optional(),
    description: z.string().trim().max(2000).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const listSubJourneysQuerySchema = z.object({
    sectionId: objectIdSchema.optional(),
});
/* ───────────────────────── admin: episodes ───────────────────────── */
/**
 * An episode is a kural: a number that is unique within the whole journey (1..1330), two
 * lines, and a meaning. The story underneath carries the audio and the banner, and its
 * title and description are kept in step with the lines and meaning so the lock screen and
 * the portal's player show the right thing.
 */
export const episodeResponseSchema = z.object({
    id: objectIdSchema,
    journeyId: objectIdSchema,
    sectionId: objectIdSchema,
    subJourneyId: objectIdSchema,
    number: z.number().int().min(1),
    line1: z.string(),
    line2: z.string(),
    meaning: z.string(),
    story: storyResponseSchema,
    createdAt: isoDateSchema,
});
export const createEpisodeBodySchema = z.object({
    /** Omit to take the next free number in the journey. */
    number: z.number().int().min(1).max(100_000).optional(),
    line1: z.string().trim().min(1).max(300),
    line2: z.string().trim().max(300).default(''),
    meaning: z.string().trim().max(4000).default(''),
});
export const updateEpisodeBodySchema = z
    .object({
    number: z.number().int().min(1).max(100_000).optional(),
    line1: z.string().trim().min(1).max(300).optional(),
    line2: z.string().trim().max(300).optional(),
    meaning: z.string().trim().max(4000).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
/* ───────────────────────── app: state ───────────────────────── */
/**
 * An episode's state for a user. The same vocabulary as a day minus LOCKED, because nothing
 * in a collection ever is. A user who has not touched an episode has no row at all, which
 * reads as AVAILABLE.
 */
export const episodeStatusSchema = z.enum(['AVAILABLE', 'IN_PROGRESS', 'COMPLETED']);
/** A row in any episode list: an adhikaram's ten, a search result, Recently Listened. */
export const episodeCardSchema = z.object({
    id: objectIdSchema,
    number: z.number().int(),
    line1: z.string(),
    line2: z.string(),
    sectionId: objectIdSchema,
    subJourneyId: objectIdSchema,
    subJourneyNumber: z.number().int(),
    subJourneyTitle: z.string(),
    durationSec: z.number().int(),
    hasAudio: z.boolean(),
    status: episodeStatusSchema,
    favourite: z.boolean(),
    completedAt: isoDateSchema.nullable(),
    lastListenedAt: isoDateSchema.nullable(),
});
/** Just enough to render a Previous / Next button (artboard 8i). */
export const episodeLinkSchema = z.object({
    id: objectIdSchema,
    number: z.number().int(),
    line1: z.string(),
});
/* ───────────────────────── app: browsing ───────────────────────── */
export const collectionSectionCardSchema = z.object({
    id: objectIdSchema,
    order: z.number().int(),
    title: z.string(),
    description: z.string(),
    /** Fallback glyph; the app shows `imageUrl` when there is one. */
    icon: z.string(),
    imageUrl: z.string().nullable(),
    subJourneyCount: z.number().int(),
    episodeCount: z.number().int(),
    /** This user's finished episodes in the section. Zero when they have not joined. */
    completedCount: z.number().int(),
});
/**
 * Artboard 8c, in one call: the journey, whether this user has joined, where they stand,
 * the sections to pick from, and the two personal shelves.
 */
export const collectionOverviewSchema = z.object({
    journey: journeySummarySchema,
    /** Null until they tap Join. Browsing never needs it; playing and progress do. */
    userStatus: userJourneyStatusSchema.nullable(),
    joined: z.boolean(),
    totals: z.object({
        sections: z.number().int(),
        subJourneys: z.number().int(),
        episodes: z.number().int(),
    }),
    completedCount: z.number().int(),
    progressPct: z.number().int().min(0).max(100),
    sections: z.array(collectionSectionCardSchema),
    /** Most recently listened first. At most ten. */
    recent: z.array(episodeCardSchema),
    /** Most recently favourited first. At most ten; `favouriteCount` has the real number. */
    favourites: z.array(episodeCardSchema),
    favouriteCount: z.number().int(),
});
/**
 * `episodeCount` is the RECORDED episodes — the ones the app can play. A collection fills
 * up over months, so a sub-journey with none yet is shown as coming soon, not hidden.
 */
export const collectionSubJourneyCardSchema = z.object({
    id: objectIdSchema,
    sectionId: objectIdSchema,
    number: z.number().int(),
    title: z.string(),
    description: z.string(),
    episodeCount: z.number().int(),
    completedCount: z.number().int(),
    durationSec: z.number().int(),
    /** The adhikaram's banner (artboard 8e), signed per read. */
    bannerUrl: z.string().nullable(),
});
/** Artboard 8d: one section and its sub-journeys. */
export const collectionSectionDetailSchema = z.object({
    section: collectionSectionCardSchema,
    subJourneys: z.array(collectionSubJourneyCardSchema),
});
/** Artboard 8e: one sub-journey and its episodes, in number order. */
export const collectionSubJourneyDetailSchema = z.object({
    section: collectionSectionCardSchema,
    subJourney: collectionSubJourneyCardSchema,
    episodes: z.array(episodeCardSchema),
});
/**
 * Artboards 8f and 8g: every episode in order, or a search.
 *
 * `q` matches an episode number exactly, or any of the two lines and the meaning as a
 * substring — a Tamil word is usually typed as it appears inside a line, which stemming
 * would not help with and a substring does. Filtered by section when a chip is active.
 */
export const episodesQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    sectionId: objectIdSchema.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const episodesPageSchema = z.object({
    items: z.array(episodeCardSchema),
    nextCursor: z.string().nullable(),
    /** Everything that matched, not just this page — the screen says "N results". */
    total: z.number().int(),
});
/** Artboards 8h and 8i: the whole kural, where the user left it, and its neighbours. */
export const episodeDetailSchema = episodeCardSchema.extend({
    meaning: z.string(),
    storyId: objectIdSchema,
    /** The episode's own banner, else its adhikaram's, else the journey cover. */
    bannerUrl: z.string().nullable(),
    positionSec: z.number().int(),
    listenedSec: z.number().int(),
    section: collectionSectionCardSchema.pick({ id: true, title: true, icon: true, imageUrl: true }),
    previous: episodeLinkSchema.nullable(),
    next: episodeLinkSchema.nullable(),
});
/** A collection as the Explore tab lists it: the pitch, plus where this user stands. */
export const collectionListItemSchema = z.object({
    journey: journeySummarySchema,
    joined: z.boolean(),
    userStatus: userJourneyStatusSchema.nullable(),
    /** Recorded episodes — what the app can play today. */
    episodeCount: z.number().int(),
    subJourneyCount: z.number().int(),
    completedCount: z.number().int(),
    progressPct: z.number().int().min(0).max(100),
});
export const collectionsResponseSchema = z.object({
    items: z.array(collectionListItemSchema),
});
/* ───────────────────────── app: joining and playing ───────────────────────── */
/** Joining is idempotent: tapping Join on a collection already joined returns it. */
export const joinCollectionResponseSchema = collectionOverviewSchema;
/**
 * An episode's playback link. Like the day story's, it is signed per read with a 15-minute
 * life and it can return 402 — `canAccess` runs here too, so the paywall stays one function
 * even though it now has two doors.
 */
export const episodePlaybackResponseSchema = z.object({
    episodeId: objectIdSchema,
    storyId: objectIdSchema,
    number: z.number().int(),
    line1: z.string(),
    line2: z.string(),
    meaning: z.string(),
    url: z.string().min(1),
    expiresAt: isoDateSchema,
    durationSec: z.number().int(),
    bannerUrl: z.string().nullable(),
    positionSec: z.number().int(),
    listenedSec: z.number().int(),
});
/**
 * The listening heartbeat for an episode — the same shape and the same rule as a day's:
 * a delta since the last beat, and `ended` is what completes it.
 */
export const episodeProgressBodySchema = z.object({
    positionSec: z.number().int().min(0),
    listenedDeltaSec: z.number().int().min(0).max(120),
    ended: z.boolean().optional(),
});
/**
 * Artboard 8j, in one payload when the beat is the one that finishes the episode: the
 * streak, the new count out of the total, and the next kural to continue to.
 */
export const episodeProgressResponseSchema = z.object({
    episodeId: objectIdSchema,
    status: episodeStatusSchema,
    listenedSec: z.number().int(),
    positionSec: z.number().int(),
    episodeCompleted: z.boolean(),
    journeyCompleted: z.boolean(),
    completedCount: z.number().int(),
    totalCount: z.number().int(),
    progressPct: z.number().int().min(0).max(100),
    /** Null unless episodeCompleted. */
    streak: z.number().int().nullable(),
    /** True only for the first completion of the user's local day. See progressResponseSchema. */
    streakAdvanced: z.boolean(),
    /** The next episode by number, or null at the end of the collection. */
    next: episodeLinkSchema.nullable(),
    achievements: z.array(unlockedAchievementSchema),
});
export const favouriteEpisodeBodySchema = z.object({ favourite: z.boolean() });
export const favouriteEpisodeResponseSchema = z.object({
    episodeId: objectIdSchema,
    favourite: z.boolean(),
});
//# sourceMappingURL=collection.js.map